import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, BookOpen, CheckCircle, ArrowRight, Mail } from 'lucide-react';

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    mobile: '',
    targetYear: new Date().getFullYear() + 1,
    preferredSubjects: ['mathematics', 'physics', 'chemistry']
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [registered, setRegistered] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const { register } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await register(formData);
      // If register didn't throw, show the "check your email" screen
      setRegisteredEmail(formData.email);
      setRegistered(true);
    } catch (error) {
      // Error is handled in the register function (toast shown there)
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const nextStep = () => {
    if (step < 2) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const isStep1Valid = formData.name && formData.email && formData.password;
  const isStep2Valid = formData.mobile && formData.targetYear;

  // Show "check your email" screen after successful registration
  if (registered) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-6">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <BookOpen className="h-8 w-8 text-white" />
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-secondary-100 p-8">
            <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="h-8 w-8 text-primary-600" />
            </div>
            <h2 className="text-2xl font-bold text-secondary-900 mb-2">Check your email</h2>
            <p className="text-secondary-600 mb-1">We sent a verification link to</p>
            <p className="font-semibold text-secondary-900 mb-6">{registeredEmail}</p>
            <p className="text-sm text-secondary-500 mb-6">
              Click the link in the email to activate your account. The link expires in 24 hours.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left mb-6">
              <p className="text-sm text-amber-800 font-medium mb-1">Didn't receive it?</p>
              <p className="text-sm text-amber-700">Check your spam folder, or sign in to request a new link.</p>
            </div>
            <Link to="/login" className="btn-primary w-full btn-lg inline-block text-center">
              Go to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex">
      {/* Left Side - Progress & Benefits */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-600 to-primary-800 p-12 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 flex flex-col justify-center max-w-lg">
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <BookOpen className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Mock Test Nation</h1>
                <p className="text-primary-100">Excellence Platform</p>
              </div>
            </div>
            <h2 className="text-4xl font-bold mb-4 leading-tight">
              Start Your
              <span className="block gradient-text-accent">Success Journey</span>
            </h2>
            <p className="text-xl text-primary-100 leading-relaxed">
              Create your account and join the community of successful JEE aspirants.
            </p>
          </div>

          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-6">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                step >= 1 ? 'bg-white text-primary-600' : 'bg-white/20 text-white'
              }`}>
                {step > 1 ? <CheckCircle className="h-5 w-5" /> : '1'}
              </div>
              <div className={`flex-1 h-1 rounded ${step > 1 ? 'bg-white' : 'bg-white/20'}`}></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                step >= 2 ? 'bg-white text-primary-600' : 'bg-white/20 text-white'
              }`}>
                2
              </div>
            </div>
            <div className="flex justify-between text-sm">
              <span className={step >= 1 ? 'text-white' : 'text-primary-200'}>Basic Info</span>
              <span className={step >= 2 ? 'text-white' : 'text-primary-200'}>Preferences</span>
            </div>
          </div>

          {/* Benefits */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-white flex-shrink-0" />
              <span>Access to 10,000+ JEE questions</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-white flex-shrink-0" />
              <span>AI-powered performance analytics</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-white flex-shrink-0" />
              <span>Personalized study recommendations</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-white flex-shrink-0" />
              <span>Progress tracking and gamification</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Registration Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="lg:hidden mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <BookOpen className="h-8 w-8 text-white" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-secondary-900 mb-2">Create Account</h2>
            <p className="text-secondary-600">Join thousands of successful JEE aspirants</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {step === 1 && (
              <div className="space-y-6 animate-fade-in-up">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">
                    Full Name
                  </label>
                  <input
                    name="name"
                    type="text"
                    required
                    className="input"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email Address
                  </label>
                  <input
                    name="email"
                    type="email"
                    required
                    className="input"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password" className="form-label">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      className="input pr-12"
                      placeholder="Create a strong password"
                      value={formData.password}
                      onChange={handleChange}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-4 text-secondary-400 hover:text-secondary-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  <p className="form-help">
                    Password should be at least 8 characters long
                  </p>
                </div>

                <button
                  type="button"
                  onClick={nextStep}
                  disabled={!isStep1Valid}
                  className="btn-primary w-full btn-lg"
                >
                  Continue
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6 animate-fade-in-up">
                <div className="form-group">
                  <label htmlFor="mobile" className="form-label">
                    Mobile Number
                  </label>
                  <input
                    name="mobile"
                    type="tel"
                    required
                    className="input"
                    placeholder="Enter your mobile number"
                    value={formData.mobile}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="targetYear" className="form-label">
                    Target JEE Year
                  </label>
                  <select
                    name="targetYear"
                    className="input"
                    value={formData.targetYear}
                    onChange={handleChange}
                  >
                    <option value={new Date().getFullYear() + 1}>
                      {new Date().getFullYear() + 1}
                    </option>
                    <option value={new Date().getFullYear() + 2}>
                      {new Date().getFullYear() + 2}
                    </option>
                    <option value={new Date().getFullYear() + 3}>
                      {new Date().getFullYear() + 3}
                    </option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Preferred Subjects</label>
                  <div className="grid grid-cols-1 gap-3 mt-2">
                    {['Mathematics', 'Physics', 'Chemistry'].map((subject) => (
                      <label key={subject} className="flex items-center p-3 border border-secondary-200 rounded-xl hover:bg-secondary-50 cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={formData.preferredSubjects.includes(subject.toLowerCase())}
                          onChange={(e) => {
                            const subjectLower = subject.toLowerCase();
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                preferredSubjects: [...formData.preferredSubjects, subjectLower]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                preferredSubjects: formData.preferredSubjects.filter(s => s !== subjectLower)
                              });
                            }
                          }}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                        />
                        <span className="ml-3 text-sm font-medium text-secondary-700">{subject}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="btn-secondary flex-1 btn-lg"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !isStep2Valid}
                    className="btn-primary flex-1 btn-lg"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Creating...
                      </div>
                    ) : (
                      'Create Account'
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>

          <div className="mt-8 text-center">
            <p className="text-secondary-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-semibold text-primary-600 hover:text-primary-500 transition-colors"
              >
                Sign in here
              </Link>
            </p>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-secondary-500">
              By creating an account, you agree to our{' '}
              <Link to="/terms" className="text-primary-600 hover:text-primary-500">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-primary-600 hover:text-primary-500">
                Privacy Policy
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}